/**
 * Citation order for the numbered styles: are the references numbered in the order
 * they are first cited, and what would the numbers be if they were?
 *
 * IEEE, Vancouver (ICMJE: "numbered consecutively in the order in which they are first
 * mentioned in the text") and AMA all number references by first citation. This reads
 * the in-text citations of a paper, compares the order in which each number first
 * appears with the numbers themselves, cross-checks them against the reference list,
 * and builds the renumbering — a map from old numbers to new, the text with every
 * citation rewritten, and the list re-sorted under its new numbers.
 *
 * Citation forms read:
 * - square brackets: [1], [2–4], [1, 3, 5], [3, p. 5] (the locator is kept), and the
 *   IEEE range written across brackets, [1]–[4];
 * - parentheses: (1), (1-3), (1, 3) — numbers only, so (2019) and (n = 12) are text;
 * - superscripts: Unicode superscript digits, a caret (^1, ^{2,3}), or digits glued to
 *   the end of a sentence or word the way a pasted superscript arrives ("shown.1,2",
 *   "previously reported3"). Glued digits are read only where that is unambiguous:
 *   after closing punctuation that does not follow a digit or an abbreviation such as
 *   "p." or "Fig.", or after a lowercase word of three or more letters.
 */

import {
  duplicateFindings,
  type Finding,
  type ListEntry,
  type ListRule,
  type NumberMarker,
  numberingFindings,
  parseNumberedList,
} from "@/lib/tool-kit/reference-checkers/numbered-list";

export type CitationFormat = "bracket" | "paren" | "superscript";

type GroupKind =
  | "bracket"
  | "bracket-range"
  | "paren"
  | "caret"
  | "unicode"
  | "glued";

export interface CitationGroup {
  kind: GroupKind;
  /** Offsets of the replaceable part in the original text. */
  start: number;
  end: number;
  /** The numbers cited, in written order, ranges expanded. */
  numbers: number[];
  /** The group wrote a range ("2–4"), so a renumbered run may be compressed again. */
  ranged: boolean;
  /** The dash the writer used in ranges. */
  dash: string;
  /** The separator between numbers: ", " or ",". */
  separator: string;
  /** Text after the numbers inside brackets: ", p. 5". */
  locator: string;
}

/** The largest number read as a citation; four-digit numbers are years. */
const MAX_CITATION = 999;
/** A written range longer than this is not a citation range. */
const MAX_RANGE_SPAN = 200;

// --- reading citations -------------------------------------------------------------

const RE_BRACKET_RANGE = /\[(\d{1,3})\]\s*([–—-])\s*\[(\d{1,3})\]/g;
const RE_BRACKET = /\[([^[\]\n]{1,80})\]/g;
const RE_PAREN = /\(([\d\s,;–—-]{1,60})\)/g;
const RE_CARET = /\^\{?(\d{1,3}(?:\s*[,–—-]\s*\d{1,3})*)\}?/g;
const RE_UNICODE = /[⁰¹²³⁴⁵⁶⁷⁸⁹]+(?:[,⁻–—-][⁰¹²³⁴⁵⁶⁷⁸⁹]+)*/g;
const RE_GLUED =
  /(?<=[\p{Ll}.,;:)\]”’"])(\d{1,3}(?:[,–—-]\d{1,3})*)(?=[\s.,;:!?)\]]|$)/gu;
const RE_TOKEN_SPLIT = /[,;]/;
const RE_NUMBER = /^\d{1,3}$/;
const RE_RANGE = /^(\d{1,3})\s*([–—-])\s*(\d{1,3})$/;
const RE_LOCATOR_START = /^\p{L}/u;
const RE_COMMA_SPACE = /,\s/;
const RE_WORD_BEFORE = /([\p{L}]+)$/u;
const RE_DIGIT = /\d/;
const RE_LETTER = /\p{L}/u;

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

/** Abbreviations whose period is followed by a number that is not a citation. */
const NUMBER_ABBREVIATIONS = new Set([
  "p",
  "pp",
  "vol",
  "no",
  "fig",
  "figs",
  "eq",
  "eqs",
  "ch",
  "chap",
  "sec",
  "sect",
  "ref",
  "refs",
  "art",
  "tab",
  "ex",
  "app",
  "para",
  "v",
  "vs",
  "nos",
  "op",
]);

interface ParsedNumbers {
  numbers: number[];
  ranged: boolean;
  dash: string;
  locator: string;
}

function expand(first: number, last: number): number[] | null {
  if (last <= first || last - first > MAX_RANGE_SPAN) {
    return null;
  }
  const out: number[] = [];
  for (let n = first; n <= last; n++) {
    out.push(n);
  }
  return out;
}

/** "1, 3–5" → [1, 3, 4, 5]; a token starting with a letter begins the locator. */
function parseNumberTokens(
  inner: string,
  allowLocator: boolean
): ParsedNumbers | null {
  const numbers: number[] = [];
  let ranged = false;
  let dash = "–";
  let consumed = 0;
  for (const raw of inner.split(RE_TOKEN_SPLIT)) {
    const token = raw.trim();
    const single = RE_NUMBER.test(token) ? Number(token) : null;
    const range = RE_RANGE.exec(token);
    if (single !== null && single > 0) {
      numbers.push(single);
    } else if (range) {
      const run = expand(Number(range[1]), Number(range[3]));
      if (!run) {
        return null;
      }
      numbers.push(...run);
      ranged = true;
      dash = range[2];
    } else if (
      allowLocator &&
      numbers.length > 0 &&
      RE_LOCATOR_START.test(token)
    ) {
      return { numbers, ranged, dash, locator: inner.slice(consumed) };
    } else {
      return null;
    }
    consumed += raw.length + 1;
  }
  return numbers.length > 0 ? { numbers, ranged, dash, locator: "" } : null;
}

/** ", " unless the writer packed commas without spaces ("1,2,3", superscript style). */
function separatorOf(inner: string): string {
  if (!(inner.includes(",") || inner.includes(";"))) {
    return ", ";
  }
  return RE_COMMA_SPACE.test(inner) || inner.includes(";") ? ", " : ",";
}

function overlaps(
  groups: readonly CitationGroup[],
  start: number,
  end: number
): boolean {
  return groups.some((group) => start < group.end && end > group.start);
}

function readBrackets(text: string): CitationGroup[] {
  const groups: CitationGroup[] = [];
  for (const match of text.matchAll(RE_BRACKET_RANGE)) {
    const run = expand(Number(match[1]), Number(match[3]));
    if (run) {
      groups.push({
        kind: "bracket-range",
        start: match.index,
        end: match.index + match[0].length,
        numbers: run,
        ranged: true,
        dash: match[2],
        separator: ", ",
        locator: "",
      });
    }
  }
  for (const match of text.matchAll(RE_BRACKET)) {
    const start = match.index;
    const end = start + match[0].length;
    const parsed = parseNumberTokens(match[1], true);
    if (parsed && !overlaps(groups, start, end)) {
      groups.push({
        kind: "bracket",
        start,
        end,
        ...parsed,
        separator: separatorOf(match[1]),
      });
    }
  }
  return groups.sort((a, b) => a.start - b.start);
}

function readParens(text: string): CitationGroup[] {
  const groups: CitationGroup[] = [];
  for (const match of text.matchAll(RE_PAREN)) {
    const parsed = parseNumberTokens(match[1], false);
    if (parsed) {
      groups.push({
        kind: "paren",
        start: match.index,
        end: match.index + match[0].length,
        ...parsed,
        separator: separatorOf(match[1]),
      });
    }
  }
  return groups;
}

function fromSuperscript(value: string): string {
  let out = "";
  for (const char of value) {
    const digit = SUPERSCRIPT_DIGITS.indexOf(char);
    if (digit >= 0) {
      out += String(digit);
    } else {
      out += char === "⁻" ? "-" : char;
    }
  }
  return out;
}

/** Glued digits count only where nothing else reads as the number's owner. */
function gluedIsCitation(text: string, index: number): boolean {
  const before = text[index - 1];
  if (RE_LETTER.test(before)) {
    const word = RE_WORD_BEFORE.exec(
      text.slice(Math.max(0, index - 40), index)
    );
    return Boolean(word && word[1].length >= 3);
  }
  // Closing punctuation: not a decimal ("2.5") and not "p.5" or "Fig.3".
  if (RE_DIGIT.test(text[index - 2] ?? "")) {
    return false;
  }
  const word = RE_WORD_BEFORE.exec(
    text.slice(Math.max(0, index - 41), index - 1)
  );
  return !(word && NUMBER_ABBREVIATIONS.has(word[1].toLowerCase()));
}

function superscriptGroup(
  kind: GroupKind,
  start: number,
  end: number,
  inner: string
): CitationGroup | null {
  const parsed = parseNumberTokens(inner, false);
  return parsed
    ? { kind, start, end, ...parsed, separator: separatorOf(inner) }
    : null;
}

function readSuperscripts(text: string): CitationGroup[] {
  const groups: CitationGroup[] = [];
  const add = (group: CitationGroup | null) => {
    if (group && !overlaps(groups, group.start, group.end)) {
      groups.push(group);
    }
  };
  for (const match of text.matchAll(RE_CARET)) {
    add(
      superscriptGroup(
        "caret",
        match.index,
        match.index + match[0].length,
        match[1]
      )
    );
  }
  for (const match of text.matchAll(RE_UNICODE)) {
    add(
      superscriptGroup(
        "unicode",
        match.index,
        match.index + match[0].length,
        fromSuperscript(match[0])
      )
    );
  }
  for (const match of text.matchAll(RE_GLUED)) {
    if (gluedIsCitation(text, match.index)) {
      add(
        superscriptGroup(
          "glued",
          match.index,
          match.index + match[0].length,
          match[1]
        )
      );
    }
  }
  return groups.sort((a, b) => a.start - b.start);
}

function inRange(group: CitationGroup): boolean {
  return group.numbers.every((n) => n >= 1 && n <= MAX_CITATION);
}

/** Every citation group of one format, in text order. */
export function readCitations(
  text: string,
  format: CitationFormat
): CitationGroup[] {
  const readers = {
    bracket: readBrackets,
    paren: readParens,
    superscript: readSuperscripts,
  } as const;
  return readers[format](text).filter(inRange);
}

/** The format with the most citations in the text — brackets on a tie. */
export function detectFormat(text: string): CitationFormat {
  let best: CitationFormat = "bracket";
  let most = 0;
  for (const format of ["bracket", "paren", "superscript"] as const) {
    const count = readCitations(text, format).length;
    if (count > most) {
      best = format;
      most = count;
    }
  }
  return best;
}

// --- the order check ---------------------------------------------------------------

export type RenumberStatus = "same" | "moved" | "never-cited" | "no-reference";

export interface RenumberRow {
  old: number;
  /** The number it takes when references follow first citation. */
  new: number;
  status: RenumberStatus;
}

export interface CitedBefore {
  number: number;
  /** Lower numbers that are first cited later. */
  before: number[];
}

export interface OrderReport {
  format: CitationFormat;
  groups: CitationGroup[];
  /** Total numbers cited, counting repeats. */
  citationCount: number;
  /** Distinct numbers in order of first citation. */
  firstCited: number[];
  hasList: boolean;
  /** The reference list as parsed, numbered by label (or position). */
  list: ListEntry[];
  outOfOrder: CitedBefore[];
  neverCited: number[];
  missingReferences: number[];
  /** Without a list: numbers below the highest citation that are never cited. */
  skipped: number[];
  mapping: RenumberRow[];
  changed: number;
  renumberedText: string;
  renumberedList: string;
  /** The reference list's own numbering problems and repeated sources. */
  listFindings: Finding<ListRule, "list">[];
}

function firstCitations(groups: readonly CitationGroup[]): number[] {
  const seen = new Set<number>();
  const order: number[] = [];
  for (const group of groups) {
    for (const n of group.numbers) {
      if (!seen.has(n)) {
        seen.add(n);
        order.push(n);
      }
    }
  }
  return order;
}

function outOfOrderCitations(firstCited: readonly number[]): CitedBefore[] {
  const issues: CitedBefore[] = [];
  for (const [index, n] of firstCited.entries()) {
    const later = firstCited.slice(index + 1).filter((m) => m < n);
    if (later.length > 0) {
      issues.push({ number: n, before: later.sort((a, b) => a - b) });
    }
  }
  return issues;
}

function listNumber(entry: ListEntry): number {
  return entry.label ?? entry.position;
}

function buildMapping(
  firstCited: readonly number[],
  listNumbers: readonly number[]
): RenumberRow[] {
  const listed = new Set(listNumbers);
  const rows: RenumberRow[] = firstCited.map((old, index) => ({
    old,
    new: index + 1,
    status: "same",
  }));
  const cited = new Set(firstCited);
  let next = rows.length + 1;
  for (const old of [...new Set(listNumbers)]) {
    if (!cited.has(old)) {
      rows.push({ old, new: next, status: "never-cited" });
      next++;
    }
  }
  for (const row of rows) {
    if (
      row.status === "same" &&
      listNumbers.length > 0 &&
      !listed.has(row.old)
    ) {
      row.status = "no-reference";
    } else if (row.status === "same" && row.old !== row.new) {
      row.status = "moved";
    }
  }
  return rows;
}

// --- writing the renumbered text and list ----------------------------------------

/** "1, 2, 3, 5" → "1–3, 5" when the writer used ranges. */
function formatNumbers(
  numbers: readonly number[],
  group: CitationGroup
): string {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  if (!group.ranged) {
    return sorted.join(group.separator);
  }
  const parts: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) {
      j++;
    }
    parts.push(
      j - i >= 2
        ? `${sorted[i]}${group.dash}${sorted[j]}`
        : sorted.slice(i, j + 1).join(group.separator)
    );
    i = j + 1;
  }
  return parts.join(group.separator);
}

function toSuperscript(value: string): string {
  let out = "";
  for (const char of value) {
    const digit = Number.parseInt(char, 10);
    out += Number.isNaN(digit) ? char : SUPERSCRIPT_DIGITS[digit];
  }
  return out;
}

function bracketRange(
  numbers: readonly number[],
  group: CitationGroup
): string {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  const consecutive = sorted.every(
    (n, i) => i === 0 || n === sorted[i - 1] + 1
  );
  return consecutive
    ? `[${sorted[0]}]${group.dash}[${sorted.at(-1)}]`
    : sorted.map((n) => `[${n}]`).join(", ");
}

function rewriteGroup(
  group: CitationGroup,
  numbers: readonly number[]
): string {
  const body = formatNumbers(numbers, group);
  switch (group.kind) {
    case "bracket-range":
      return bracketRange(numbers, group);
    case "bracket":
      return `[${body}${group.locator ? `,${group.locator}` : ""}]`;
    case "paren":
      return `(${body})`;
    case "caret":
      return `^${body}`;
    case "unicode":
      return toSuperscript(body);
    default:
      return body;
  }
}

function renumberText(
  text: string,
  groups: readonly CitationGroup[],
  map: ReadonlyMap<number, number>
): string {
  let out = "";
  let cursor = 0;
  for (const group of groups) {
    out += text.slice(cursor, group.start);
    out += rewriteGroup(
      group,
      group.numbers.map((n) => map.get(n) ?? n)
    );
    cursor = group.end;
  }
  return out + text.slice(cursor);
}

function writeMarker(marker: NumberMarker | null, n: number): string {
  switch (marker) {
    case "bracket":
      return `[${n}]`;
    case "paren":
      return `(${n})`;
    case "close":
      return `${n})`;
    case "bare":
      return `${n}`;
    default:
      return `${n}.`;
  }
}

function renumberList(
  list: readonly ListEntry[],
  map: ReadonlyMap<number, number>
): string {
  const used = new Set<number>();
  const rows: { n: number; line: string }[] = [];
  let extra = map.size + 1;
  for (const entry of list) {
    const old = listNumber(entry);
    let n = map.get(old);
    if (n === undefined || used.has(old)) {
      // A duplicate number keeps its text and goes to the end.
      n = extra++;
    }
    used.add(old);
    const marker = entry.marker ?? list.find((e) => e.marker)?.marker ?? null;
    rows.push({ n, line: `${writeMarker(marker, n)} ${entry.text}` });
  }
  return rows
    .sort((a, b) => a.n - b.n)
    .map((row) => row.line)
    .join("\n");
}

function skippedNumbers(firstCited: readonly number[]): number[] {
  const cited = new Set(firstCited);
  const highest = Math.max(0, ...firstCited);
  const skipped: number[] = [];
  for (let n = 1; n <= highest; n++) {
    if (!cited.has(n)) {
      skipped.push(n);
    }
  }
  return skipped;
}

/**
 * Check the citation order of `text` against the numbered `references` (which may be
 * empty: the order is still checked, and the renumbering still built).
 */
export function checkCitationOrder(
  text: string,
  references: string,
  format: CitationFormat
): OrderReport {
  const groups = readCitations(text, format);
  const firstCited = firstCitations(groups);
  const list = parseNumberedList(references);
  const hasList = list.length > 0;
  const listNumbers = list.map(listNumber);
  const listed = new Set(listNumbers);
  const mapping = buildMapping(firstCited, listNumbers);
  const map = new Map(mapping.map((row) => [row.old, row.new]));
  return {
    format,
    groups,
    citationCount: groups.reduce((sum, group) => sum + group.numbers.length, 0),
    firstCited,
    hasList,
    list,
    outOfOrder: outOfOrderCitations(firstCited),
    neverCited: mapping
      .filter((row) => row.status === "never-cited")
      .map((row) => row.old),
    missingReferences: hasList
      ? firstCited.filter((n) => !listed.has(n)).sort((a, b) => a - b)
      : [],
    skipped: hasList ? [] : skippedNumbers(firstCited),
    mapping,
    changed: mapping.filter((row) => row.old !== row.new).length,
    renumberedText: renumberText(text, groups, map),
    renumberedList: hasList ? renumberList(list, map) : "",
    listFindings: hasList
      ? [
          ...numberingFindings(list, "list").filter(
            (finding) => finding.rule !== "unnumbered"
          ),
          ...duplicateFindings(list, "list"),
        ]
      : [],
  };
}
